/**
 * AI Draft Merge — parse LLM output back into the 7 preview sections,
 * build a per-section diff against the current structured answers, and
 * apply only the sections the veteran approves.
 *
 * Design choice: overrides live in the answers map under synthetic keys
 * `__ai_override_<sectionId>`. They are PREVIEW-ONLY — the original
 * structured answers are never mutated, so the vet can revert at any
 * time. SectionedPreview prefers `__ai_override_*` when present.
 *
 * Public API:
 *   - parseAiSections(rawOutput): { [sectionId]: content }
 *   - buildSectionDiff(prompts, answers, parsed): [{ sectionId, title, currentText, proposedText, hasChange, defaultSelected }]
 *   - applyOverrides(answers, parsed, sectionIds): newAnswers
 *   - clearOverrides(answers): newAnswers
 *   - hasAnyOverride(answers): boolean
 *   - OVERRIDE_PREFIX: "__ai_override_"
 */

import { PREVIEW_SECTIONS, PREVIEW_SECTION_ORDER } from "./statementAudit";

export const OVERRIDE_PREFIX = "__ai_override_";

// Section ID → list of regex patterns that match the header in LLM output.
// We allow:
//   - ALL CAPS (the system prompt requests this)
//   - Title Case (in case the model strays)
//   - Trailing colon or em-dash
//   - Leading "#" markdown headers (in case the model wraps as markdown)
const SECTION_HEADER_PATTERNS = {
  "condition":        [/^[#\s]*statement\s+of\s+condition[\s:.\-—]*$/im],
  "service-onset":    [/^[#\s]*service\s+onset(?:\s*[/&]\s*event)?[\s:.\-—]*$/im, /^[#\s]*onset\s*[/&]\s*event[\s:.\-—]*$/im],
  "symptoms-service": [/^[#\s]*symptoms?\s+during\s+service[\s:.\-—]*$/im, /^[#\s]*in[- ]service\s+symptoms?[\s:.\-—]*$/im],
  "continuity":       [/^[#\s]*continuity\s+since\s+service[\s:.\-—]*$/im, /^[#\s]*continuity[\s:.\-—]*$/im],
  "current-impact":   [/^[#\s]*current\s+functional\s+impact[\s:.\-—]*$/im, /^[#\s]*current\s+impact[\s:.\-—]*$/im, /^[#\s]*functional\s+impact[\s:.\-—]*$/im],
  "treatment":        [/^[#\s]*treatment\s*[/&]\s*medication[\s:.\-—]*$/im, /^[#\s]*treatment[\s:.\-—]*$/im],
  "closing":          [/^[#\s]*closing\s+statement[\s:.\-—]*$/im, /^[#\s]*closing[\s:.\-—]*$/im],
};

/**
 * Walk the raw LLM output line-by-line. Each time we hit a recognised
 * section header, we close the previous section and start collecting
 * lines for the new one. Whitespace + the literal "[Not yet provided]"
 * marker (which the system prompt tells the model to emit for empty
 * sections) are stripped from the trailing output.
 */
export function parseAiSections(rawOutput) {
  if (!rawOutput || typeof rawOutput !== "string") return {};
  // Strip outer code fences if any survived the backend trimmer.
  let text = rawOutput.trim();
  if (text.startsWith("```")) text = text.replace(/^```[a-z]*\s*/i, "").replace(/```\s*$/, "").trim();

  const lines = text.split(/\r?\n/);
  const sections = {};
  let currentId = null;
  let buf = [];

  function flush() {
    if (!currentId) return;
    const content = buf.join("\n").trim();
    // "[Not yet provided]" is the placeholder the system prompt uses for empty sections.
    if (content && !/^\[\s*not\s+yet\s+provided\s*\]\s*$/i.test(content)) {
      sections[currentId] = content;
    }
    buf = [];
  }

  for (const line of lines) {
    let matched = null;
    for (const [sid, patterns] of Object.entries(SECTION_HEADER_PATTERNS)) {
      if (patterns.some((re) => re.test(line))) { matched = sid; break; }
    }
    if (matched) {
      flush();
      currentId = matched;
      continue;
    }
    if (currentId) buf.push(line);
  }
  flush();
  return sections;
}

/**
 * Build the current text for one section using the same logic as
 * buildSectioned in StatementsView.jsx (group answers by prompt.section).
 * NB: we skip override keys so the diff compares apples to apples.
 */
function buildCurrentSectionText(prompts, answers, sectionId) {
  const items = prompts
    .filter((p) => (p.section || "") === sectionId)
    .map((p) => (answers[p.id] || "").trim())
    .filter(Boolean);
  return items.join("\n\n");
}

/**
 * Returns an ordered list of diff rows (one per relevant section).
 * - Skips sections the AI didn't produce.
 * - `defaultSelected = true` when current content is empty (safe to fill in)
 *    OR when the section is "current-impact" / "treatment" (these are the most
 *    common rewrite targets and the diff is the whole point of opening the
 *    modal). For non-empty existing content we still default ON, but the
 *    UI shows the diff so the vet can opt out.
 */
export function buildSectionDiff(prompts, answers, parsedSections) {
  const out = [];
  for (const sid of PREVIEW_SECTION_ORDER) {
    if (sid === "condition" || sid === "closing") continue; // opener/closing are static
    const proposedText = (parsedSections?.[sid] || "").trim();
    if (!proposedText) continue;
    const currentText = buildCurrentSectionText(prompts, answers, sid);
    out.push({
      sectionId: sid,
      title: PREVIEW_SECTIONS[sid],
      currentText,
      proposedText,
      hasChange: currentText !== proposedText,
      defaultSelected: true,
    });
  }
  return out;
}

/**
 * Apply only the chosen sections to the answers map. Returns a NEW answers
 * object — original is not mutated.
 */
export function applyOverrides(answers, parsedSections, sectionIds) {
  const next = { ...(answers || {}) };
  for (const sid of sectionIds || []) {
    const content = (parsedSections?.[sid] || "").trim();
    if (content) next[`${OVERRIDE_PREFIX}${sid}`] = content;
  }
  return next;
}

/**
 * Strip all AI overrides — used by the "Revert AI changes" affordance.
 */
export function clearOverrides(answers) {
  const next = {};
  for (const [k, v] of Object.entries(answers || {})) {
    if (!k.startsWith(OVERRIDE_PREFIX)) next[k] = v;
  }
  return next;
}

/**
 * Quick check used by the editor toolbar to decide whether to render
 * the "Revert AI changes" button.
 */
export function hasAnyOverride(answers) {
  return Object.keys(answers || {}).some((k) => k.startsWith(OVERRIDE_PREFIX) && (answers[k] || "").trim() !== "");
}

/**
 * For SectionedPreview building — given an answers map, return the override
 * for a section (or null). Lets the preview builder pick the override when
 * present without duplicating the lookup logic.
 */
export function getOverride(answers, sectionId) {
  if (!answers || !sectionId) return null;
  const v = answers[`${OVERRIDE_PREFIX}${sectionId}`];
  return v && v.trim() ? v : null;
}
